import { HttpClient } from "@/services/httpClient";
import { searchFrappeDoctype } from "@/api/frappeLookupApi";
import { Controllers, FrappeMethodType } from "@/type/api";
import type {
  CashierListItem,
  PosProfileAssignedUser,
  PosProfileDetail,
  PosProfilePaymentMethod,
  PosProfilePayload,
  PosProfileSummary,
} from "@/type/posProfile";
import { ref } from "vue";

const posProfileService = new HttpClient(Controllers.posProfile);
const cashierMethodService = new HttpClient(Controllers.account);
const POS_PROFILE_DOCTYPE = "POS Profile";
const POS_PROFILE_FIELDS = JSON.stringify(["name", "company", "warehouse", "customer"]);
const CASHIER_LIST_METHOD = "pet_app.api.accounting.cashier.list_cashier_profiles_for_user";
const CASHIER_DETAIL_METHOD = "pet_app.api.accounting.cashier.get_cashier_profile_detail";
const CASHIER_SAVE_METHOD = "pet_app.api.accounting.cashier.save_cashier_profile";
const ACCOUNTING_SETTINGS_METHOD = "pet_app.api.accounting.settings.get_accounting_settings";

interface FrappeMetaField {
  fieldname?: string | null;
  fieldtype?: string | null;
  options?: string | null;
}

interface FrappeMetaDoc {
  fields?: FrappeMetaField[] | null;
}

interface PosProfilePaymentRow {
  mode_of_payment?: string | null;
  default?: number | string | boolean | null;
  is_default?: number | string | boolean | null;
  default_mode?: number | string | boolean | null;
  default_for_pos?: number | string | boolean | null;
  default_account?: string | null;
  account?: string | null;
  cash_bank_account?: string | null;
  [key: string]: unknown;
}

interface PosProfileUserRow {
  user?: string | null;
  owner?: string | null;
  email?: string | null;
  username?: string | null;
  [key: string]: unknown;
}

interface PosProfileDoc {
  name?: string | null;
  company?: string | null;
  warehouse?: string | null;
  /** ERPNext names this `customer` ("Default Customer"); some builds expose `default_customer`. */
  customer?: string | null;
  default_customer?: string | null;
  selling_price_list?: string | null;
  price_list?: string | null;
  currency?: string | null;
  price_list_currency?: string | null;
  payments?: PosProfilePaymentRow[] | null;
  mode_of_payments?: PosProfilePaymentRow[] | null;
  applicable_for_users?: PosProfileUserRow[] | null;
  users?: PosProfileUserRow[] | null;
  [key: string]: unknown;
}

interface PosProfileSchema {
  paymentTableFieldname: string;
  paymentChildDoctype: string | null;
  paymentModeFieldname: string;
  paymentDefaultFieldname: string | null;
  paymentAccountFieldname: string | null;
  userTableFieldname: string;
  userChildDoctype: string | null;
  userLinkFieldname: string;
}

interface ModeOfPaymentAccountRow {
  company?: string | null;
  default_account?: string | null;
  account?: string | null;
  cash_bank_account?: string | null;
  [key: string]: unknown;
}

interface ModeOfPaymentDoc {
  name?: string | null;
  accounts?: ModeOfPaymentAccountRow[] | null;
  mode_of_payment_accounts?: ModeOfPaymentAccountRow[] | null;
  [key: string]: unknown;
}

interface ModeOfPaymentSchema {
  accountTableFieldname: string;
  accountChildDoctype: string | null;
  companyFieldname: string;
  accountFieldname: string;
}

let cachedSchema: PosProfileSchema | null = null;
let cachedModeOfPaymentSchema: ModeOfPaymentSchema | null = null;

function toBoolean(value: unknown) {
  if (typeof value === "boolean")
    return value;

  if (typeof value === "number")
    return value === 1;

  return ["1", "true", "yes"].includes(String(value ?? "").trim().toLowerCase());
}

function normalizeString(value: unknown) {
  const normalized = String(value ?? "").trim();
  return normalized || null;
}

function toNumber(value: unknown) {
  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) ? numeric : 0;
}

function extractAccountName(value: unknown) {
  if (!value)
    return null;

  if (typeof value === "string")
    return normalizeString(value);

  if (typeof value === "object") {
    const row = value as Record<string, unknown>;
    return normalizeString(
      row.name
      ?? row.account
      ?? row.account_name
      ?? row.accountName,
    );
  }

  return null;
}

function mapPosProfileSummary(doc: PosProfileDoc): PosProfileSummary {
  const name = normalizeString(doc.name) || "";

  return {
    id: name,
    name,
    company: normalizeString(doc.company),
    warehouse: normalizeString(doc.warehouse),
    assignedUsers: [],
  };
}

function resolvePaymentRows(doc: PosProfileDoc) {
  if (Array.isArray(doc.payments))
    return doc.payments;

  if (Array.isArray(doc.mode_of_payments))
    return doc.mode_of_payments;

  return [];
}

function resolveUserRows(doc: PosProfileDoc) {
  if (Array.isArray(doc.applicable_for_users))
    return doc.applicable_for_users;

  if (Array.isArray(doc.users))
    return doc.users;

  return [];
}

function mapPaymentMethod(row: PosProfilePaymentRow): PosProfilePaymentMethod | null {
  const modeOfPayment = normalizeString(row.mode_of_payment);
  if (!modeOfPayment)
    return null;

  return {
    modeOfPayment,
    account: normalizeString(
      row.default_account
      ?? row.account
      ?? row.cash_bank_account,
    ),
    isDefault: toBoolean(
      row.default
      ?? row.is_default
      ?? row.default_mode
      ?? row.default_for_pos,
    ),
  };
}

function mapAssignedUser(row: PosProfileUserRow): PosProfileAssignedUser | null {
  const id = normalizeString(
    row.user
    ?? row.email
    ?? row.username
    ?? row.owner,
  );

  if (!id)
    return null;

  return {
    id,
    label: id,
  };
}

function buildPosProfileDetail(doc: PosProfileDoc): PosProfileDetail {
  const summary = mapPosProfileSummary(doc);
  const paymentMethods = resolvePaymentRows(doc)
    .map(mapPaymentMethod)
    .filter((row): row is PosProfilePaymentMethod => Boolean(row));
  const cashAccount = normalizeString(
    extractAccountName(
      doc.custom_cash_account
      ?? doc.cash_account
      ?? doc.cashAccount,
    ),
  );

  const allowedModeOfPayments = [...new Set(paymentMethods.map(row => row.modeOfPayment))];
  const defaultModeOfPayment = paymentMethods.find(row => row.isDefault)?.modeOfPayment
    ?? allowedModeOfPayments[0]
    ?? null;
  const assignedUsers = resolveUserRows(doc)
    .map(mapAssignedUser)
    .filter((row): row is PosProfileAssignedUser => Boolean(row));

  return {
    ...summary,
    cashAccount,
    sellingPriceList: normalizeString(
      doc.selling_price_list
      ?? doc.price_list
      ?? doc.sellingPriceList,
    ),
    priceListCurrency: normalizeString(
      doc.price_list_currency
      ?? doc.priceListCurrency
      ?? doc.currency,
    ),
    currency: normalizeString(doc.currency),
    defaultCustomer: normalizeString(
      doc.customer
      ?? doc.default_customer
      ?? doc.defaultCustomer,
    ),
    defaultModeOfPayment,
    allowedModeOfPayments,
    paymentMethods,
    assignedUsers,
  };
}

function resolveCashAccount(profile: PosProfileDetail) {
  return profile.cashAccount ?? null;
}

function unwrapMethodPayload<T>(response: any): T {
  return (response?.message ?? response?.data ?? response ?? null) as T;
}

function normalizeAllowedModeOfPayments(value: unknown) {
  if (!Array.isArray(value))
    return [];

  return [...new Set(
    value
      .map(item => String(item ?? "").trim())
      .filter(Boolean),
  )];
}

function normalizePaymentMethodEntry(entry: unknown): PosProfilePaymentMethod | null {
  if (!entry || typeof entry !== "object")
    return null;

  const row = entry as Record<string, unknown>;
  const modeOfPayment = normalizeString(
    row.mode_of_payment
    ?? row.modeOfPayment
    ?? row.payment_method
    ?? row.paymentMethod,
  );

  if (!modeOfPayment)
    return null;

  return {
    modeOfPayment,
    account: normalizeString(
      row.account
      ?? row.default_account
      ?? row.cash_bank_account
      ?? row.cashAccount,
    ),
    isDefault: toBoolean(
      row.is_default
      ?? row.isDefault
      ?? row.default
      ?? row.default_mode
      ?? row.default_for_pos,
    ),
  };
}

function normalizeAssignedUserEntry(entry: unknown): PosProfileAssignedUser | null {
  if (typeof entry === "string") {
    const id = normalizeString(entry);

    return id
      ? {
          id,
          label: id,
        }
      : null;
  }

  if (!entry || typeof entry !== "object")
    return null;

  const row = entry as Record<string, unknown>;
  const id = normalizeString(
    row.id
    ?? row.user
    ?? row.email
    ?? row.username
    ?? row.name,
  );

  if (!id)
    return null;

  return {
    id,
    label: normalizeString(
      row.label
      ?? row.full_name
      ?? row.fullName
      ?? row.email
      ?? row.user
      ?? row.name,
    ) || id,
  };
}

function buildSyntheticPaymentMethods(
  allowedModeOfPayments: string[],
  defaultModeOfPayment: string | null,
  cashAccount: string | null,
): PosProfilePaymentMethod[] {
  const defaultMode = normalizeString(defaultModeOfPayment);

  return allowedModeOfPayments.map((mode, index) => ({
    modeOfPayment: mode,
    account: mode.toLowerCase() === "cash" ? cashAccount : null,
    isDefault: defaultMode ? mode === defaultMode : index === 0,
  }));
}

function mapCashierProfilePayload(payload: any): PosProfileDetail {
  const rawPaymentMethods: unknown[] = Array.isArray(payload?.payment_methods)
    ? payload.payment_methods
    : Array.isArray(payload?.paymentMethods)
      ? payload.paymentMethods
      : Array.isArray(payload?.payments)
        ? payload.payments
        : [];

  const paymentMethods = rawPaymentMethods
    .map(normalizePaymentMethodEntry)
    .filter((entry: PosProfilePaymentMethod | null): entry is PosProfilePaymentMethod => Boolean(entry));

  const rawAssignedUsers: unknown[] = Array.isArray(payload?.assigned_users)
    ? payload.assigned_users
    : Array.isArray(payload?.assignedUsers)
      ? payload.assignedUsers
      : Array.isArray(payload?.applicable_for_users)
        ? payload.applicable_for_users
        : Array.isArray(payload?.users)
          ? payload.users
        : [];

  const assignedUsers = rawAssignedUsers
    .map(normalizeAssignedUserEntry)
    .filter((entry: PosProfileAssignedUser | null): entry is PosProfileAssignedUser => Boolean(entry));

  const cashAccount = normalizeString(
    extractAccountName(
      payload?.cash_account
      ?? payload?.custom_cash_account
      ?? payload?.cashAccount,
    ),
  );
  const sellingPriceList = normalizeString(
    payload?.selling_price_list
    ?? payload?.sellingPriceList
    ?? payload?.price_list
    ?? payload?.priceList,
  );
  const priceListCurrency = normalizeString(
    payload?.price_list_currency
    ?? payload?.priceListCurrency
    ?? payload?.currency,
  );
  const currency = normalizeString(payload?.currency);
  /**
   * ⚠️ MAPPED HERE TOO, NOT ONLY IN `mapPosProfileDoc`. POS reads its profile through
   * the cashier-detail method, so a default customer mapped only on the doctype path
   * would never reach the till.
   */
  const defaultCustomer = normalizeString(
    payload?.customer
    ?? payload?.default_customer
    ?? payload?.defaultCustomer,
  );

  const allowedModeOfPayments: string[] = normalizeAllowedModeOfPayments(
    payload?.allowed_mode_of_payments ?? payload?.allowedModeOfPayments,
  );

  const defaultModeOfPayment = normalizeString(
    payload?.default_mode_of_payment
    ?? payload?.default_cash_mode_of_payment
    ?? payload?.defaultModeOfPayment,
  ) ?? paymentMethods.find(method => method.isDefault)?.modeOfPayment
    ?? allowedModeOfPayments[0]
    ?? null;

  const normalizedPaymentMethods = paymentMethods.length
    ? paymentMethods.map((method: PosProfilePaymentMethod) => ({
        ...method,
        account: method.account ?? (method.modeOfPayment.toLowerCase() === "cash" ? cashAccount : null),
      }))
    : buildSyntheticPaymentMethods(allowedModeOfPayments, defaultModeOfPayment, cashAccount);

  return {
    id: normalizeString(payload?.name ?? payload?.profile_name ?? payload?.profileName) || "",
    name: normalizeString(payload?.name ?? payload?.profile_name ?? payload?.profileName) || "",
    company: normalizeString(payload?.company),
    warehouse: normalizeString(payload?.warehouse),
    assignedUsers,
    cashAccount,
    sellingPriceList,
    priceListCurrency,
    currency,
    defaultCustomer,
    cashAccountBalance: toNumber(
      payload?.cash_account_balance
      ?? payload?.cashAccountBalance
      ?? payload?.current_balance
      ?? payload?.currentBalance,
    ),
    defaultModeOfPayment,
    allowedModeOfPayments: allowedModeOfPayments.length
      ? allowedModeOfPayments
      : [...new Set(normalizedPaymentMethods.map((method: PosProfilePaymentMethod) => method.modeOfPayment))],
    paymentMethods: normalizedPaymentMethods,
  };
}

async function callCashierGetMethod<T>(method: string, params: Record<string, unknown> = {}) {
  const response = await cashierMethodService.get<any>(
    method,
    {},
    params,
    undefined,
    undefined,
    FrappeMethodType.method,
  );

  return unwrapMethodPayload<T>(response);
}

async function callCashierPostMethod<T>(method: string, payload: Record<string, unknown>) {
  const response = await cashierMethodService.post<any>(
    payload,
    method,
    {},
    undefined,
    FrappeMethodType.method,
  );

  return unwrapMethodPayload<T>(response);
}

async function fetchCashierProfileList() {
  const payload = await callCashierGetMethod<any>(CASHIER_LIST_METHOD);

  if (Array.isArray(payload))
    return payload;

  if (Array.isArray(payload?.data))
    return payload.data;

  if (Array.isArray(payload?.profiles))
    return payload.profiles;

  // Envelope (#16) wraps raw lists as `data.items` / `data.result`.
  if (Array.isArray(payload?.items))
    return payload.items;

  if (Array.isArray(payload?.result))
    return payload.result;

  return [];
}

async function getFrappeMeta(doctype: string) {
  const response = await posProfileService.get<any>(
    "frappe.desk.form.load.getdoctype",
    {},
    {
      doctype,
      with_parent: 1,
    },
    undefined,
    undefined,
    FrappeMethodType.method,
  );

  const payload = response?.message ?? response?.data ?? response ?? null;
  const docs = Array.isArray(payload?.docs)
    ? payload.docs
    : Array.isArray(payload)
      ? payload
      : [];

  return docs.find((doc: any) => Array.isArray(doc?.fields))
    ?? payload?.meta
    ?? null;
}

async function resolvePosProfileSchema(): Promise<PosProfileSchema> {
  if (cachedSchema)
    return cachedSchema;

  const posProfileMeta = await getFrappeMeta(POS_PROFILE_DOCTYPE) as FrappeMetaDoc | null;
  const paymentTableField = posProfileMeta?.fields?.find(field =>
    field.fieldtype === "Table"
    && ["payments", "mode_of_payments"].includes(String(field.fieldname ?? "")),
  ) ?? posProfileMeta?.fields?.find(field => field.fieldtype === "Table");

  const paymentChildDoctype = normalizeString(paymentTableField?.options);
  let paymentModeFieldname = "mode_of_payment";
  let paymentDefaultFieldname: string | null = "default";
  let paymentAccountFieldname: string | null = "default_account";
  let userTableFieldname = "applicable_for_users";
  let userChildDoctype: string | null = null;
  let userLinkFieldname = "user";

  if (paymentChildDoctype) {
    const paymentMeta = await getFrappeMeta(paymentChildDoctype) as FrappeMetaDoc | null;
    const childFields = paymentMeta?.fields ?? [];

    paymentModeFieldname = childFields.find(field =>
      ["mode_of_payment", "payment_method"].includes(String(field.fieldname ?? "")),
    )?.fieldname || paymentModeFieldname;

    paymentDefaultFieldname = childFields.find(field =>
      ["default", "is_default", "default_mode", "default_for_pos"].includes(String(field.fieldname ?? "")),
    )?.fieldname || null;

    paymentAccountFieldname = childFields.find(field =>
      ["default_account", "account", "cash_bank_account"].includes(String(field.fieldname ?? "")),
    )?.fieldname || null;
  }

  const userTableField = posProfileMeta?.fields?.find(field =>
    field.fieldtype === "Table"
    && ["applicable_for_users", "users"].includes(String(field.fieldname ?? "")),
  ) ?? posProfileMeta?.fields?.find((field) => {
    if (field.fieldtype !== "Table")
      return false;

    const fieldname = String(field.fieldname ?? "").toLowerCase();
    const options = String(field.options ?? "").toLowerCase();

    return fieldname.includes("user") || options.includes("user");
  });

  userTableFieldname = String(userTableField?.fieldname ?? userTableFieldname);
  userChildDoctype = normalizeString(userTableField?.options);

  if (userChildDoctype) {
    const userMeta = await getFrappeMeta(userChildDoctype) as FrappeMetaDoc | null;
    const childFields = userMeta?.fields ?? [];

    userLinkFieldname = childFields.find(field =>
      field.fieldtype === "Link"
      && String(field.options ?? "") === "User",
    )?.fieldname || childFields.find(field =>
      ["user", "email", "username", "owner"].includes(String(field.fieldname ?? "")),
    )?.fieldname || userLinkFieldname;
  }

  cachedSchema = {
    paymentTableFieldname: String(paymentTableField?.fieldname ?? "payments"),
    paymentChildDoctype,
    paymentModeFieldname,
    paymentDefaultFieldname,
    paymentAccountFieldname,
    userTableFieldname,
    userChildDoctype,
    userLinkFieldname,
  };

  return cachedSchema;
}

function resolveModeOfPaymentAccountRows(doc: ModeOfPaymentDoc) {
  if (Array.isArray(doc.accounts))
    return doc.accounts;

  if (Array.isArray(doc.mode_of_payment_accounts))
    return doc.mode_of_payment_accounts;

  return [];
}

async function resolveModeOfPaymentSchema(): Promise<ModeOfPaymentSchema> {
  if (cachedModeOfPaymentSchema)
    return cachedModeOfPaymentSchema;

  const modeOfPaymentMeta = await getFrappeMeta("Mode of Payment") as FrappeMetaDoc | null;
  const accountTableField = modeOfPaymentMeta?.fields?.find(field =>
    field.fieldtype === "Table"
    && (
      String(field.options ?? "") === "Mode of Payment Account"
      || String(field.fieldname ?? "").toLowerCase().includes("account")
    ),
  ) ?? modeOfPaymentMeta?.fields?.find(field => field.fieldtype === "Table");

  const accountChildDoctype = normalizeString(accountTableField?.options);
  let companyFieldname = "company";
  let accountFieldname = "default_account";

  if (accountChildDoctype) {
    const accountMeta = await getFrappeMeta(accountChildDoctype) as FrappeMetaDoc | null;
    const childFields = accountMeta?.fields ?? [];

    companyFieldname = childFields.find(field =>
      ["company"].includes(String(field.fieldname ?? "")),
    )?.fieldname || companyFieldname;

    accountFieldname = childFields.find(field =>
      ["default_account", "account", "cash_bank_account"].includes(String(field.fieldname ?? "")),
    )?.fieldname || accountFieldname;
  }

  cachedModeOfPaymentSchema = {
    accountTableFieldname: String(accountTableField?.fieldname ?? "accounts"),
    accountChildDoctype,
    companyFieldname,
    accountFieldname,
  };

  return cachedModeOfPaymentSchema;
}

async function fetchPosProfileDoc(name: string) {
  const response = await posProfileService.get<any>(
    "frappe.client.get",
    {},
    {
      doctype: POS_PROFILE_DOCTYPE,
      name,
    },
    undefined,
    undefined,
    FrappeMethodType.method,
  );

  return response?.message ?? response?.data ?? response ?? null;
}

async function fetchModeOfPaymentDoc(name: string) {
  const response = await posProfileService.get<any>(
    "frappe.client.get",
    {},
    {
      doctype: "Mode of Payment",
      name,
    },
    undefined,
    undefined,
    FrappeMethodType.method,
  );

  return response?.message ?? response?.data ?? response ?? null;
}

function buildPaymentRows(
  paymentMethods: PosProfilePaymentMethod[],
  schema: PosProfileSchema,
) {
  return paymentMethods.map((method, index) => {
    const row: Record<string, unknown> = {};
    const account = method.account || undefined;
    const isDefault = method.isDefault || index === 0 ? 1 : 0;

    if (schema.paymentChildDoctype)
      row.doctype = schema.paymentChildDoctype;

    row[schema.paymentModeFieldname] = method.modeOfPayment;
    row.mode_of_payment = method.modeOfPayment;

    if (schema.paymentAccountFieldname)
      row[schema.paymentAccountFieldname] = account;

    if (account) {
      row.default_account = account;
      row.account = account;
      row.cash_bank_account = account;
    }

    if (schema.paymentDefaultFieldname)
      row[schema.paymentDefaultFieldname] = isDefault;

    row.default = isDefault;
    row.is_default = isDefault;
    row.default_mode = isDefault;
    row.default_for_pos = isDefault;

    return row;
  });
}

function buildAssignedUserRows(
  assignedUsers: PosProfileAssignedUser[],
  schema: PosProfileSchema,
) {
  return assignedUsers.map((assignedUser) => {
    const row: Record<string, unknown> = {};

    if (schema.userChildDoctype)
      row.doctype = schema.userChildDoctype;

    row[schema.userLinkFieldname] = assignedUser.id;

    return row;
  });
}

function normalizePaymentMethods(paymentMethods: PosProfilePaymentMethod[]) {
  const filtered = paymentMethods
    .map(method => ({
      modeOfPayment: String(method.modeOfPayment ?? "").trim(),
      account: normalizeString(method.account),
      isDefault: Boolean(method.isDefault),
    }))
    .filter(method => method.modeOfPayment);

  if (!filtered.length)
    return [];

  const defaultIndex = filtered.findIndex(method => method.isDefault);
  return filtered.map((method, index) => ({
    ...method,
    isDefault: defaultIndex === -1 ? index === 0 : index === defaultIndex,
  }));
}

function normalizeAssignedUsers(assignedUsers: PosProfileAssignedUser[]) {
  const seen = new Set<string>();

  return assignedUsers
    .map(user => ({
      id: String(user.id ?? "").trim(),
      label: String(user.label ?? user.id ?? "").trim() || String(user.id ?? "").trim(),
    }))
    .filter((user) => {
      if (!user.id || seen.has(user.id))
        return false;

      seen.add(user.id);
      return true;
    });
}

async function syncModeOfPaymentAccount(
  modeOfPayment: string,
  company: string,
  account: string | null,
) {
  const normalizedMode = String(modeOfPayment ?? "").trim();
  const normalizedCompany = String(company ?? "").trim();
  const normalizedAccount = normalizeString(account);

  if (!normalizedMode || !normalizedCompany || !normalizedAccount)
    return;

  const [schema, existingDoc] = await Promise.all([
    resolveModeOfPaymentSchema(),
    fetchModeOfPaymentDoc(normalizedMode),
  ]);

  if (!existingDoc)
    return;

  const currentRows = resolveModeOfPaymentAccountRows(existingDoc).map(row => ({ ...row }));
  const existingRow = currentRows.find((row) => {
    const rowCompany = normalizeString(
      row[schema.companyFieldname]
      ?? row.company,
    );

    return rowCompany === normalizedCompany;
  });

  if (existingRow) {
    existingRow[schema.companyFieldname] = normalizedCompany;
    existingRow[schema.accountFieldname] = normalizedAccount;
    existingRow.default_account = normalizedAccount;
    existingRow.account = normalizedAccount;
    existingRow.cash_bank_account = normalizedAccount;
  } else {
    const row: Record<string, unknown> = {
      [schema.companyFieldname]: normalizedCompany,
      [schema.accountFieldname]: normalizedAccount,
      company: normalizedCompany,
      default_account: normalizedAccount,
      account: normalizedAccount,
      cash_bank_account: normalizedAccount,
    };

    if (schema.accountChildDoctype)
      row.doctype = schema.accountChildDoctype;

    currentRows.push(row);
  }

  const updatedDoc = {
    ...existingDoc,
    doctype: "Mode of Payment",
    [schema.accountTableFieldname]: currentRows,
  };

  await posProfileService.post<any>(
    {
      doc: JSON.stringify(updatedDoc),
    },
    "frappe.client.save",
    {},
    undefined,
    FrappeMethodType.method,
  );
}

async function syncModeOfPaymentAccounts(
  paymentMethods: PosProfilePaymentMethod[],
  company: string,
) {
  const seen = new Set<string>();

  await Promise.all(
    paymentMethods.map(async (method) => {
      const mode = String(method.modeOfPayment ?? "").trim();
      const dedupeKey = `${company}::${mode}`;

      if (!mode || seen.has(dedupeKey))
        return;

      seen.add(dedupeKey);
      await syncModeOfPaymentAccount(mode, company, method.account);
    }),
  );
}

/**
 * The ERPNext-native "which account does this payment mode post to" lookup:
 * `Mode of Payment` → its `Mode of Payment Account` child row for this company.
 *
 * Schema-agnostic on purpose — `resolveModeOfPaymentSchema` introspects the child
 * doctype's own fieldnames via meta rather than hardcoding `default_account`,
 * because that name has moved between ERPNext versions.
 *
 * Exported so the payment dialog can resolve a cashier's account instead of
 * asking them to pick a GL account by hand.
 */
export async function resolveModeOfPaymentAccount(
  modeOfPayment: string,
  company: string | null,
) {
  const normalizedMode = String(modeOfPayment ?? "").trim();
  const normalizedCompany = String(company ?? "").trim();

  if (!normalizedMode || !normalizedCompany)
    return null;

  const [schema, doc] = await Promise.all([
    resolveModeOfPaymentSchema(),
    fetchModeOfPaymentDoc(normalizedMode),
  ]);

  const rows = doc ? resolveModeOfPaymentAccountRows(doc) : [];
  const row = rows.find((item) => {
    const rowCompany = normalizeString(
      item[schema.companyFieldname]
      ?? item.company,
    );

    return rowCompany === normalizedCompany;
  });

  return normalizeString(
    row?.[schema.accountFieldname]
    ?? row?.default_account
    ?? row?.account
    ?? row?.cash_bank_account,
  );
}

async function hydratePosProfileDetail(doc: PosProfileDoc): Promise<PosProfileDetail> {
  const detail = buildPosProfileDetail(doc);
  const hydratedPaymentMethods = await Promise.all(
    detail.paymentMethods.map(async (method) => ({
      ...method,
      account: method.account || await resolveModeOfPaymentAccount(method.modeOfPayment, detail.company),
    })),
  );

  return {
    ...detail,
    paymentMethods: hydratedPaymentMethods,
  };
}

function mergePosProfileDetails(
  primary: PosProfileDetail,
  fallback: PosProfileDetail | null,
): PosProfileDetail {
  if (!fallback)
    return primary;

  return {
    ...primary,
    company: primary.company || fallback.company,
    warehouse: primary.warehouse || fallback.warehouse,
    cashAccount: primary.cashAccount || fallback.cashAccount,
    sellingPriceList: primary.sellingPriceList || fallback.sellingPriceList,
    priceListCurrency: primary.priceListCurrency || fallback.priceListCurrency,
    currency: primary.currency || fallback.currency,
    defaultCustomer: primary.defaultCustomer || fallback.defaultCustomer,
    defaultModeOfPayment: primary.defaultModeOfPayment || fallback.defaultModeOfPayment,
    allowedModeOfPayments: primary.allowedModeOfPayments.length
      ? primary.allowedModeOfPayments
      : fallback.allowedModeOfPayments,
    paymentMethods: primary.paymentMethods.length
      ? primary.paymentMethods.map((method) => {
          const fallbackMethod = fallback.paymentMethods.find(row =>
            row.modeOfPayment === method.modeOfPayment,
          );

          return {
            ...method,
            account: method.account || fallbackMethod?.account || null,
          };
        })
      : fallback.paymentMethods,
    assignedUsers: primary.assignedUsers.length
      ? primary.assignedUsers
      : fallback.assignedUsers,
  };
}

export async function GetPosProfiles(): Promise<PosProfileSummary[]> {
  try {
    try {
      const profiles = await fetchCashierProfileList();

      if (profiles.length) {
        return profiles
          .map(mapCashierProfilePayload)
          .filter((profile: PosProfileDetail) => profile.name)
          .map((profile: PosProfileDetail) => ({
            id: profile.id,
            name: profile.name,
            company: profile.company,
            warehouse: profile.warehouse,
            assignedUsers: profile.assignedUsers,
          }));
      }
    } catch (methodError) {
      console.error(methodError);
    }

    const response = await posProfileService.get<{ data?: PosProfileDoc[] }>(
      "",
      {
        limit_start: 0,
        limit_page_length: 100,
        order_by: "modified desc",
      },
      {},
      POS_PROFILE_FIELDS,
    );

    return (response?.data ?? [])
      .map(mapPosProfileSummary)
      .filter(profile => profile.name);
  } catch (error) {
    console.error(error);
    return [];
  }
}

export async function GetPosProfileDetail(profileName: string): Promise<PosProfileDetail | null> {
  const name = String(profileName ?? "").trim();
  if (!name)
    return null;

  try {
    try {
      const payload = await callCashierGetMethod<any>(CASHIER_DETAIL_METHOD, {
        profile: name,
        profile_name: name,
      });

      if (payload) {
        const methodDetail = mapCashierProfilePayload(payload);
        const fallbackDoc = await fetchPosProfileDoc(name).catch(() => null);
        const fallbackDetail = fallbackDoc
          ? await hydratePosProfileDetail(fallbackDoc).catch(() => null)
          : null;

        return mergePosProfileDetails(methodDetail, fallbackDetail);
      }
    } catch (methodError) {
      console.error(methodError);
    }

    const doc = await fetchPosProfileDoc(name);
    if (!doc)
      return null;

    return hydratePosProfileDetail(doc);
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function GetCashierProfiles(): Promise<CashierListItem[]> {
  try {
    const listedProfiles = await fetchCashierProfileList();

    if (listedProfiles.length) {
      const mappedProfiles = listedProfiles
        .map(mapCashierProfilePayload)
        .filter((profile: PosProfileDetail) => profile.name);

      const details = await Promise.all(
        mappedProfiles.map(async (profile: PosProfileDetail) => {
          const detail = await GetPosProfileDetail(profile.name);
          return detail ?? profile;
        }),
      );

      return details.map((profile: PosProfileDetail) => ({
        ...profile,
        cashAccount: resolveCashAccount(profile),
        currentBalance: 0,
        todayIncoming: 0,
        todayOutgoing: 0,
        expectedCashOnHand: 0,
      }));
    }
  } catch (error) {
    console.error(error);
  }

  const profiles = await GetPosProfiles();
  const details = await Promise.all(
    profiles.map(profile => GetPosProfileDetail(profile.name)),
  );

  return details
    .filter((profile): profile is PosProfileDetail => Boolean(profile))
    .map(profile => ({
      ...profile,
      cashAccount: resolveCashAccount(profile),
      currentBalance: 0,
      todayIncoming: 0,
      todayOutgoing: 0,
      expectedCashOnHand: 0,
    }));
}

export async function GetDefaultCompany(): Promise<string | null> {
  try {
    const payload = await callCashierGetMethod<any>(ACCOUNTING_SETTINGS_METHOD);
    const defaultCompany = normalizeString(
      payload?.default_company ?? payload?.defaultCompany,
    );

    if (defaultCompany)
      return defaultCompany;
  } catch (error) {
    console.error(error);
  }

  const result = await searchFrappeDoctype(ref(false), {
    doctype: "Company",
    page: 1,
    fields: ["company_name", "default_currency"],
    searchFields: ["name", "company_name"],
    labelField: "company_name",
    subtitleFields: ["default_currency", "name"],
    limit: 1,
    orderBy: "creation asc",
  });

  return result.items[0]?.id ?? null;
}

export async function CreatePosProfile(payload: PosProfilePayload): Promise<PosProfileDetail | null> {
  const normalizedPaymentMethods = normalizePaymentMethods(payload.paymentMethods);
  const normalizedAssignedUsers = normalizeAssignedUsers(payload.assignedUsers);
  const cashAccount = payload.cashAccount
    ?? normalizedPaymentMethods.find(method => method.modeOfPayment.toLowerCase() === "cash")?.account
    ?? normalizedPaymentMethods.find(method => method.isDefault)?.account
    ?? null;
  const paymentMethodRows = normalizedPaymentMethods.map((method, index) => ({
    mode_of_payment: method.modeOfPayment,
    is_default: method.isDefault ? 1 : 0,
    default: method.isDefault ? 1 : 0,
    allow_in_returns: 1,
    idx: index + 1,
  }));
  const userRows = normalizedAssignedUsers.map((user) => ({
    user: user.id,
  }));
  const cashierPayload = {
    name: payload.name.trim(),
    company: payload.company,
    warehouse: payload.warehouse || null,
    custom_cash_account: cashAccount,
    cash_account: cashAccount,
    payments: paymentMethodRows,
    users: userRows,
    disabled: 0,
  };

  const saved = await callCashierPostMethod<any>(CASHIER_SAVE_METHOD, {
    profile: payload.name.trim(),
    pos_profile: payload.name.trim(),
    cashier_profile: payload.name.trim(),
    data: JSON.stringify(cashierPayload),
    ...cashierPayload,
  });

  const savedProfileName = normalizeString(
    saved?.name ?? saved?.profile_name ?? payload.name,
  );

  if (!savedProfileName)
    return saved ? mapCashierProfilePayload(saved) : null;

  return GetPosProfileDetail(savedProfileName);
}

export async function UpdatePosProfile(
  profileName: string,
  payload: PosProfilePayload,
): Promise<PosProfileDetail | null> {
  const name = String(profileName ?? "").trim();
  if (!name)
    return null;

  const normalizedPaymentMethods = normalizePaymentMethods(payload.paymentMethods);
  const normalizedAssignedUsers = normalizeAssignedUsers(payload.assignedUsers);
  const cashAccount = payload.cashAccount
    ?? normalizedPaymentMethods.find(method => method.modeOfPayment.toLowerCase() === "cash")?.account
    ?? normalizedPaymentMethods.find(method => method.isDefault)?.account
    ?? null;
  const paymentMethodRows = normalizedPaymentMethods.map((method, index) => ({
    mode_of_payment: method.modeOfPayment,
    is_default: method.isDefault ? 1 : 0,
    default: method.isDefault ? 1 : 0,
    allow_in_returns: 1,
    idx: index + 1,
  }));
  const userRows = normalizedAssignedUsers.map((user) => ({
    user: user.id,
  }));
  const cashierPayload = {
    name,
    company: payload.company,
    warehouse: payload.warehouse || null,
    custom_cash_account: cashAccount,
    cash_account: cashAccount,
    payments: paymentMethodRows,
    users: userRows,
  };

  await callCashierPostMethod<any>(CASHIER_SAVE_METHOD, {
    profile: name,
    pos_profile: name,
    cashier_profile: name,
    data: JSON.stringify(cashierPayload),
    ...cashierPayload,
  });

  return GetPosProfileDetail(name);
}
